import type { Exchange, Operation } from "@urql/vue";
import { useOnline } from "@vueuse/core";
import { ref, watchEffect } from "vue";
import { filter, onPush, pipe } from "wonka";
import { useLogger } from "@/composables/logger";

const { info } = useLogger("MUTATION_LIMITER_EXCHANGE");

const backlog: Operation[] = [];
const online = useOnline();
export const inFlight = ref(false);

export const haltUntilCompletion = (): Promise<void> =>
  new Promise<void>(resolve => {
    info("PROMISE STARTED");
    if (!online.value) resolve();
    watchEffect(() => {
      if (inFlight.value === false && backlog.length === 0) {
        info("PROMISE RESOLVED");
        resolve();
      }
    });
  });

export const mutationLimiterExchange: Exchange =
  ({ client, forward }) =>
  operations$ => {
    const operationResult$ = pipe(
      operations$,
      filter(op => {
        // pass through queries, subscriptions & teardowns
        // pass through mutations if offline
        if (op.kind !== "mutation" || !online.value) return true;
        // if any response is pending throw on backlog
        else if (inFlight.value) {
          backlog.push(op);
          info("Operation discarded, Sent to backlog", { op, backlog });
          return false;
        } else {
          inFlight.value = true;
          info("Operation Starting, blocking new operations", { op, backlog });
          return true;
        }
      }),
      forward
    );

    return pipe(
      operationResult$,
      onPush(op => {
        // ignore non-mutation operations or offline mutation
        if (op.operation.kind !== "mutation" || !online.value) return;
        // set current operation as completed
        inFlight.value = false;

        if (backlog[0]) {
          // fire off next operation
          const nextOp = backlog.shift();
          if (nextOp) client.reexecuteOperation(nextOp);
          info("Operation succeeded, Firing next operation", {
            finishingOperation: op,
            newOperation: nextOp,
            backlog,
            inFlight
          });
        } else {
          info("Operation succeeded, Backlog Empty, Waiting for new requests", {
            finishingOperation: op,
            backlog,
            inFlight
          });
        }
      })
    );
  };
